import { useState, useEffect } from "react";
import { Activity, Plus, AlertTriangle } from "lucide-react";
import { getParameterIcon } from "../../../utils/parameterIcons";
import { LiveTable } from "./LiveTable";
import { AddParameterModal, ParameterFormData } from "./AddParameterModal";
import DeleteConfirmationModal from "../../modal/DeleteConfirmationModal";
import { ParameterData } from "../../../types/device";
import { useDeviceStore } from "../../../store/useDeviceStore";
import {
  useGetDeviceParameters,
  useGetMultiDeviceParameters,
  useDeleteParameter,
} from "../../../queries/parameters";
import { useGetMultiDeviceLiveData } from "../../../queries/admin";
import useMqttStore from "../../../store/useMqttStore";
import { useValueStore } from "../../../store/useValueState";
import { brokerUrl, topicPrefix } from "../../../constant";

interface ParameterTableWidgetProps {
  isEditMode?: boolean;
}

export default function ParameterTableWidget({ isEditMode = false }: ParameterTableWidgetProps) {
  const { selectedDeviceId, selectedDeviceIds } = useDeviceStore();
  const [parameters, setParameters] = useState<ParameterData[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingParameter, setEditingParameter] = useState<ParameterData | null>(null);
  const [editModeParameters, setEditModeParameters] = useState<ParameterFormData[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [parameterToDelete, setParameterToDelete] = useState<string | null>(null);
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);

  // MQTT integration for live data
  const { connectMqtt, subscribeTopic, deviceDataMap, isConnected } = useMqttStore();
  const { metrics } = useValueStore();

  // Single-device fetch: used for edit-mode (editing is always scoped to the
  // primary selected device).
  const {
    data: singleDeviceParameters,
    isLoading: singleLoading,
    error: singleError,
  } = useGetDeviceParameters(selectedDeviceId, true);

  // Multi-device fetch: merges unique parameter definitions across every
  // selected device (deduped by identifier). Used for the live view in ALL
  // cases — even a single-device selection benefits from dedup-by-identifier
  // because a backend response may contain repeated identifiers.
  const effectiveDeviceIds =
    selectedDeviceIds.length > 0
      ? selectedDeviceIds
      : selectedDeviceId
      ? [selectedDeviceId]
      : [];
  const { data: mergedParameters } =
    useGetMultiDeviceParameters(effectiveDeviceIds);

  // Last known reading per device, used as a fallback so returning users see
  // their historical values instead of zero while MQTT reconnects/re-subscribes.
  const lastKnownDataMap = useGetMultiDeviceLiveData(effectiveDeviceIds);

  // The live view always iterates over the merged (deduped) list so that:
  //  - In multi-device mode, the union of identifiers across all selected
  //    devices is rendered (one row per identifier).
  //  - In single-device mode, any duplicate identifiers from the backend
  //    collapse into a single row.
  const backendParameters = mergedParameters;
  const isLoading = singleLoading;
  const error = singleError;

  // Only keep delete mutation as create/update are now handled in the modal
  const { mutateAsync: deleteParameterMutation } = useDeleteParameter();

  // Get live value from MQTT data, falling back to the last known reading
  // from the DB when MQTT hasn't delivered anything yet for a device — so a
  // returning user sees their historical data instead of zero, and only a
  // device with no data at all reads as empty.
  // In multi-device mode, aggregate by identifier across all selected devices
  // (average of numeric values; calibration applied per-device).
  const getAggregatedLiveValue = (
    identifier: string,
    calibration: number = 1,
    dataType: string = "FLOAT"
  ): { value: number | string | undefined; deviceCount: number } => {
    const deviceIds = selectedDeviceIds.length > 0 ? selectedDeviceIds : selectedDeviceId ? [selectedDeviceId] : [];
    if (deviceIds.length === 0) return { value: undefined, deviceCount: 0 };

    // STRING parameters are not numeric — they can't be averaged or calibrated.
    // Surface the latest raw text reading from the first device that reports it.
    if (dataType === "STRING") {
      for (const dId of deviceIds) {
        const liveData = deviceDataMap[dId]?.data as Record<string, any> | undefined;
        const lastKnown = lastKnownDataMap[dId] as Record<string, any> | null;
        const raw =
          liveData?.[identifier] !== undefined && liveData?.[identifier] !== null
            ? liveData[identifier]
            : lastKnown?.[identifier];
        if (raw !== undefined && raw !== null) {
          return { value: String(raw), deviceCount: 1 };
        }
      }
      return { value: undefined, deviceCount: 0 };
    }

    const values: number[] = [];
    for (const dId of deviceIds) {
      const liveData = deviceDataMap[dId]?.data as Record<string, any> | undefined;
      const lastKnown = lastKnownDataMap[dId] as Record<string, any> | null;
      const raw =
        liveData?.[identifier] !== undefined && liveData?.[identifier] !== null
          ? liveData[identifier]
          : lastKnown?.[identifier];
      if (raw !== undefined && raw !== null && typeof raw === "number") {
        values.push(raw * (calibration || 1));
      }
    }

    if (values.length === 0) return { value: undefined, deviceCount: 0 };
    const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
    return { value: avg, deviceCount: values.length };
  };

  // Calculate parameter status based on value and thresholds
  const calculateStatus = (
    value?: number,
    minValue?: number,
    maxValue?: number
  ): "normal" | "warning" | "critical" => {
    if (value === undefined || (minValue === undefined && maxValue === undefined)) {
      return "normal";
    }

    if (minValue !== undefined && maxValue !== undefined) {
      const range = maxValue - minValue;
      const warningThreshold = range * 0.8;

      if (value < minValue || value > maxValue) {
        return "critical";
      } else if (value > minValue + warningThreshold || value < minValue + range * 0.2) {
        return "warning";
      }
    }

    return "normal";
  };

  // Transform backend parameters to UI format with live data
  const transformBackendParameters = (params: ParameterFormData[]): ParameterData[] => {
    return params.map((param) => {
      // Aggregated value across selected devices (calibration already applied per-device).
      const { value: aggregatedValue, deviceCount } = getAggregatedLiveValue(
        param.identifier,
        param.calibration || 1,
        param.dataType
      );
      // STRING params default to "NA" (never 0) when no reading has arrived.
      const value = aggregatedValue ?? (param.dataType === "STRING" ? "NA" : 0);
      const status =
        typeof value === "number"
          ? calculateStatus(value, param.minValue, param.maxValue)
          : "normal";
      return {
        id: param.id || "",
        identifier: param.identifier,
        name: param.name,
        value,
        unit: param.unit,
        dataType: param.dataType,
        deviceId: param.deviceId,
        minValue: param.minValue,
        maxValue: param.maxValue,
        icon: param.icon, // Keep the string icon from backend
        iconElement: getParameterIcon(param.icon || param.identifier), // Use iconElement for React elements
        status,
        trend: "stable", // We can enhance this with historical data later
        trendValue: 0,
        aggregationDeviceCount: deviceCount,
        target: param.maxValue ? param.maxValue * 0.8 : undefined,
      };
    });
  };


  // // Connect to MQTT and subscribe to device topic
  // useEffect(() => {
  //   if (!isConnected) {
  //     connectMqtt(brokerUrl);
  //   }

  //   if (selectedDeviceId && isConnected) {
  //     const topic = `${topicPrefix}/${selectedDeviceId}`;
  //     subscribeTopic(topic);
  //   }
  // }, [selectedDeviceId, isConnected, connectMqtt, subscribeTopic]);

  // Update parameters when backend data or live data changes
  useEffect(() => {
    if (backendParameters) {
      setParameters(transformBackendParameters(backendParameters));
    }
    // Edit mode always operates on the single primary device's parameters.
    if (singleDeviceParameters) {
      setEditModeParameters(singleDeviceParameters);
    }
  }, [
    backendParameters,
    singleDeviceParameters,
    deviceDataMap,
    selectedDeviceId,
    selectedDeviceIds,
    lastKnownDataMap,
  ]);

  // Real-time updates for live values from MQTT
  useEffect(() => {
    const interval = setInterval(() => {
      const hasData = selectedDeviceIds.some((id) => deviceDataMap[id]?.data);
      if (backendParameters && hasData) {
        setParameters(transformBackendParameters(backendParameters));
      }
    }, 1000); // Update every second when live data is available

    return () => clearInterval(interval);
  }, [backendParameters, deviceDataMap, selectedDeviceId, selectedDeviceIds]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as Element).closest(".menu-trigger")) {
        setOpenMenuIndex(null);
      }
    };

    if (openMenuIndex !== null) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [openMenuIndex]);

  const handleAddParameter = async (parameterData: ParameterFormData) => {
    if (isEditMode) {
      // In edit mode, add to editModeParameters
      setEditModeParameters((prev) => [...prev, parameterData]);
    }
    // The modal now handles the API call, so we just need to refresh the data
    // The query will automatically refetch due to invalidation in the mutation
  };

  const handleEditParameter = (parameter: ParameterData) => {
    setEditingParameter(parameter);
    setShowAddModal(true);
  };

  const handleUpdateParameter = async (parameterData: ParameterFormData) => {
    if (editingParameter) {
      // The modal now handles the API call, so we just need to refresh the data
      // The query will automatically refetch due to invalidation in the mutation
      setEditingParameter(null);
    }
  };

  const handleDeleteParameter = (parameterId?: string) => {
    if (parameterId) {
      setParameterToDelete(parameterId);
      setShowDeleteModal(true);
    }
  };

  const confirmDeleteParameter = async () => {
    if (parameterToDelete) {
      if (selectedDeviceId) {
        // Use API to delete parameter
        try {
          await deleteParameterMutation(parameterToDelete);
        } catch (error) {
          console.error("Error deleting parameter:", error);
        }
      }
      setShowDeleteModal(false);
      setParameterToDelete(null);
    }
  };

  const handleModalSave = (parameterData: ParameterFormData) => {
    if (editingParameter) {
      handleUpdateParameter(parameterData);
    } else {
      handleAddParameter(parameterData);
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingParameter(null);
  };

  // Show loading state
  if (isLoading && selectedDeviceId && !isEditMode) {
    return (
      <div className="p-6 space-y-8 min-h-screen bg-gray-50">
        <div className="flex items-center justify-center h-64">
          <div className="text-center bg-white p-8 rounded-lg shadow-sm border border-gray-200">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Parameters</h3>
            <p className="text-gray-600">Fetching real-time sensor data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && selectedDeviceId && !isEditMode) {
    return (
      <div className="p-6 space-y-8 min-h-screen bg-gray-50">
        <div className="flex items-center justify-center h-64">
          <div className="text-center bg-white p-8 rounded-lg shadow-sm border border-gray-200">
            <div className="text-red-500 mb-4">
              <AlertTriangle className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Connection Error</h3>
            <p className="text-gray-600 mb-4">
              Unable to load parameters. Please check your connection.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
            >
              Retry Connection
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 bg-gray-50">
      {/* Parameters Table */}
      {isEditMode ? (
        <div className="space-y-4">
          {/* Header - matching the Device Detail page layout */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Parameter Configuration
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Configure and monitor device parameters
              </p>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2.5 text-sm font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add Parameter
            </button>
          </div>

          {editModeParameters.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-[#111B21] rounded-xl border border-gray-200 dark:border-gray-700">
              <Activity className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                No parameters configured
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Add parameters to start monitoring this device.
              </p>
            </div>
          ) : (
            <LiveTable
              parameters={transformBackendParameters(editModeParameters)}
              isEditMode={true}
              onEditParameter={handleEditParameter}
              onDeleteParameter={handleDeleteParameter}
              isLiveDataConnected={
                isConnected && selectedDeviceIds.some((id) => !!deviceDataMap[id]?.data)
              }
            />
          )}
        </div>
      ) : (
        <div className="space-y-8">
          <LiveTable
            parameters={
              metrics && metrics.length > 0
                ? parameters.filter((p) => metrics.includes(p.name))
                : parameters
            }
            isEditMode={false}
            onEditParameter={handleEditParameter}
            onDeleteParameter={handleDeleteParameter}
            isLiveDataConnected={
              isConnected && selectedDeviceIds.some((id) => !!deviceDataMap[id]?.data)
            }
          />
        </div>
      )}

      <AddParameterModal
        isOpen={showAddModal}
        onClose={handleCloseModal}
        onSave={(data) => handleModalSave(data)}
        editParameter={
          editingParameter
            ? {
                id: editingParameter.id,
                deviceId: editingParameter.deviceId || "",
                name: editingParameter.name,
                identifier: editingParameter.identifier,
                unit: editingParameter.unit,
                dataType: editingParameter.dataType,
                minValue: editingParameter.minValue,
                maxValue: editingParameter.maxValue,
                calibration: editingParameter.calibration,
              }
            : undefined
        }
        isEditMode={!!editingParameter}
      />

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setParameterToDelete(null);
        }}
        onConfirm={confirmDeleteParameter}
        title="Delete Parameter"
        itemType="parameter"
        variant="simple"
      />
    </div>
  );
}
